import { cloneDeep, omit } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { translateInput, useDataMutation, useUser } from '@work4all/data';
import { useEntityJsonSchema } from '@work4all/data/lib/json-schema/EntityJsonSchemasContext';

import { TravelAdditionalMealExpenses } from '@work4all/models/lib/Classes/TravelAdditionalMealExpenses.entity';
import { TravelExpenses } from '@work4all/models/lib/Classes/TravelExpenses.entity';
import { TravelReceipts } from '@work4all/models/lib/Classes/TravelReceipts.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { settings, useSetting } from '../../../../../settings';
import { useSupplier } from '../../../../files/detail/components/queries/use-supplier';
import { LockOverride } from '../../components/LockOverride';
import { useMaskConfig } from '../../hooks/mask-context';
import { OverlayController } from '../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../overlay-controller/use-mask-overlay';
import { MaskControllerProps } from '../../types';
import { pickUpdateFields } from '../../utils/pick-update-fields';
import { CurrencyExchangeInfoContextProvider } from '../inbound-invoice/CurrencyExchangeInfoContextProvider';

import { General } from './components/General';
import { useTravelExpenses } from './hooks/use-travel-expenses';
import { useTravelExpensesFormUpdate } from './hooks/use-travel-expenses-form-update';
import { TravelExpensesFormValue } from './type';
import { calculateReceiptsRelation } from './utils/calculate-receipts-relation';

const TRAVEL_EXPENSES_DATA = {
  id: null,
  date: null,
  travelStart: null,
  travelEnd: null,
  note: null,
  travelExpensesKindId: null,
  projectId: null,
  costCenterId: null,
  payoutAmount: null,
  advance: null,
  receiptSum: null,
  additionalMealExpenses: [
    {
      id: null,
      travelExpensesId: null,
      dateTime: null,
      amount: null,
      hours: null,
      deductionBreakfast: null,
      deductionBreakfastAmount: null,
      deductionLunch: null,
      deductionLunchAmount: null,
      deductionDinner: null,
      deductionDinnerAmount: null,
      projectId: null,
      businessPartnerId: null,
      businessPartnerType: null,
      note: null,
      project: {
        id: null,
        name: null,
        number: null,
      },
      company: {
        id: null,
        data: {
          customer: {
            id: null,
            name: null,
          },
          supplier: {
            id: null,
            name: null,
          },
        },
      },
    },
  ],
};

export const TravelExpensesOverlayController = (props: MaskControllerProps) => {
  return (
    <CurrencyExchangeInfoContextProvider>
      <TravelExpensesOverlayControllerInternal {...props} />
    </CurrencyExchangeInfoContextProvider>
  );
};

const TravelExpensesOverlayControllerInternal = (
  props: MaskControllerProps
) => {
  const { t } = useTranslation();

  const mask = useMaskConfig(props);

  const setting = useSetting(settings.travelExpenseDefaults());
  const { request, newEntityData } = useTravelExpenses(mask, setting.value);

  const normalizeData = useCallback(
    (
      entity: TravelExpensesFormValue,
      newEntity: TravelExpensesFormValue,
      isCreateMode: boolean
    ) => {
      return isCreateMode
        ? newEntity
        : {
            ...entity,
            businessPartner: entity?.businessPartner?.data,
            recepitsEdited: entity?.receipts,
            countryId: entity?.country?.id,
          };
    },
    []
  );

  const schema = useEntityJsonSchema(mask.entity);

  // Workaround - change schema as API wrongly parsing enums
  const patchedSchema = useMemo(() => {
    if (!schema) return schema;

    // Create a deep copy to avoid mutating the original schema
    const clonedSchema = cloneDeep(schema);

    if (Array.isArray(clonedSchema?.properties?.travelExpensesKindId?.enum)) {
      if (
        !clonedSchema.properties.travelExpensesKindId.enum.includes('STANDARD')
      ) {
        clonedSchema.properties.travelExpensesKindId.enum.push('STANDARD');
      }
      if (
        !clonedSchema.properties.travelExpensesKindId.enum.includes(
          'BELEGE_OHNE_REISE_2'
        )
      ) {
        clonedSchema.properties.travelExpensesKindId.enum.push(
          'BELEGE_OHNE_REISE_2'
        );
      }
    }
    return clonedSchema;
  }, [schema]);

  const customRules = useCallback(
    (data: TravelExpensesFormValue) => {
      if (!data.countryId && !data.country?.id) {
        return {
          country: {
            message: t('form.validation.required'),
            type: 'customValidation',
          },
        };
      }
      return true;
    },
    [t]
  );

  const overlay = useMaskOverlay<TravelExpensesFormValue>({
    ...props,
    request,
    newEntityData,
    mask,
    normalizeData,
    getSubTitle: (x) => x.note,
    schema: patchedSchema,
    customRules,
  });

  const { form, data } = overlay;
  const { formState } = form;

  const [mutate] = useDataMutation<TravelExpenses, EMode.upsert>({
    entity: mask.entity,
    mutationType: EMode.upsert,
    wrapInput: false,
    responseData:
      TRAVEL_EXPENSES_DATA as unknown as TravelExpenses<EMode.entity>,
    onCompleted: (data) => {
      props.onAfterSave(data);
    },
  });

  useTravelExpensesFormUpdate(form);

  const user = useUser();

  const { isSupplierValid } = useSupplier(user?.supplierCode?.toString());

  const handleSubmit = useCallback(
    async (input: TravelExpensesFormValue) => {
      const updateRaw = mask.isCreateMode
        ? input
        : {
            ...pickUpdateFields(input, formState.dirtyFields),
            travelEnd: input.travelEnd,
            travelStart: input.travelStart,
          };

      const additionalMealExpenses = (
        (input.additionalMealExpenses as TravelAdditionalMealExpenses[]) ?? []
      )
        .map((x) => omit(x, '__typename'))
        .map((x) =>
          translateInput(Entities.travelAdditionalMealExpenses, x, false)
        );

      // Calculate InputTravelExpensesReciptRelation from receipts (original) and recepitsEdited (final version)
      const originalReceipts = (input.receipts as TravelReceipts[]) || [];
      const editedReceipts = (input.recepitsEdited as TravelReceipts[]) || [];

      const receiptsRelation = calculateReceiptsRelation(
        originalReceipts,
        editedReceipts
      );

      // Prepare relations object
      const relations: Record<string, unknown> = {};

      if (additionalMealExpenses?.length) {
        relations.additionalMealExpenses = additionalMealExpenses;
      }

      if (
        receiptsRelation.add?.length ||
        receiptsRelation.modify?.length ||
        receiptsRelation.remove?.length
      ) {
        // Backend typo
        relations.recipts = receiptsRelation;
      }

      await mutate({
        input: translateInput(Entities.travelExpenses, updateRaw, false),
        relations: Object.keys(relations).length ? relations : undefined,
      });
    },
    [mask.isCreateMode, formState.dirtyFields, mutate]
  );

  const disabled =
    (mask.isEditMode && data?.closedByuserid) ||
    (mask.isCreateMode && !user.supplierCode) ||
    (mask.isCreateMode && !isSupplierValid);

  return (
    <LockOverride
      forceLock={Boolean(disabled)}
      lockReason={t('ALERTS.TRAVEL_RECEIPT_LOCKED')}
    >
      <OverlayController<TravelExpensesFormValue>
        {...overlay}
        onSubmit={handleSubmit}
      >
        <General />
      </OverlayController>
    </LockOverride>
  );
};
